import * as RadixDialog from '@radix-ui/react-dialog'
import type { FormEvent, ReactNode } from 'react'

import { Button } from './Button.tsx'

/**
 * Caixa de diálogo modal.
 *
 * Radix cuida do que é fácil de errar à mão: foco preso dentro do diálogo,
 * devolução do foco ao fechar, `Escape`, `aria-modal` e o resto da árvore
 * marcada como inerte. A aparência é toda daqui.
 *
 * Como as demais superfícies elevadas do console, sombra e nenhuma borda: uma
 * borda somaria um segundo separador contra as hairlines dos painéis atrás.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  /** Larga o suficiente para um formulário de duas colunas; `'xl'` para tabelas. */
  wide = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  wide?: boolean | 'xl'
}) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-canvas/75" />
        <RadixDialog.Content
          className={`fixed top-1/2 left-1/2 z-50 flex max-h-[85vh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col bg-raised shadow-overlay ${
            wide === 'xl' ? 'max-w-5xl' : wide ? 'max-w-2xl' : 'max-w-md'
          }`}
        >
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-line-subtle px-4 py-2.5">
            <div className="min-w-0">
              <RadixDialog.Title className="text-base font-medium text-fg">
                {title}
              </RadixDialog.Title>
              {description !== undefined && (
                <RadixDialog.Description className="mt-1 text-sm leading-snug text-fg-secondary">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close asChild>
              <Button variant="ghost" size="sm" aria-label="Fechar">
                ✕
              </Button>
            </RadixDialog.Close>
          </div>

          {children !== undefined && (
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">{children}</div>
          )}

          {footer !== undefined && (
            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-line-subtle px-4 py-2.5">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

/**
 * Diálogo de formulário: `<form>` de verdade, para que Enter submeta e o
 * navegador faça a validação nativa dos campos obrigatórios.
 */
export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel,
  submitVariant = 'primary',
  busy = false,
  error,
  onSubmit,
  children,
  wide = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: ReactNode
  submitLabel: string
  submitVariant?: 'primary' | 'danger'
  busy?: boolean
  /** Falha da última tentativa de envio. Fica junto do botão que a causou. */
  error?: ReactNode
  onSubmit: () => void
  children: ReactNode
  wide?: boolean
}) {
  const handle = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    onSubmit()
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-canvas/75" />
        <RadixDialog.Content
          className={`fixed top-1/2 left-1/2 z-50 flex max-h-[85vh] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col bg-raised shadow-overlay ${
            wide ? 'max-w-2xl' : 'max-w-md'
          }`}
        >
          <form onSubmit={handle} className="flex min-h-0 flex-col">
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-line-subtle px-4 py-2.5">
              <div className="min-w-0">
                <RadixDialog.Title className="text-base font-medium text-fg">
                  {title}
                </RadixDialog.Title>
                {description !== undefined && (
                  <RadixDialog.Description className="mt-1 text-sm leading-snug text-fg-secondary">
                    {description}
                  </RadixDialog.Description>
                )}
              </div>
              <RadixDialog.Close asChild>
                <Button variant="ghost" size="sm" aria-label="Fechar">
                  ✕
                </Button>
              </RadixDialog.Close>
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">{children}</div>

            <div className="shrink-0 border-t border-line-subtle px-4 py-2.5">
              {error != null && error !== '' && (
                <p role="alert" className="mb-2 text-sm leading-snug text-crit">
                  {error}
                </p>
              )}
              <div className="flex items-center justify-end gap-2">
                <RadixDialog.Close asChild>
                  <Button variant="default">Cancelar</Button>
                </RadixDialog.Close>
                <Button type="submit" variant={submitVariant} busy={busy}>
                  {busy ? 'Enviando…' : submitLabel}
                </Button>
              </div>
            </div>
          </form>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  )
}

/**
 * Confirmação de ação destrutiva.
 *
 * `consequence` é obrigatório e é a razão do componente existir: "Tem certeza?"
 * não é confirmação, é um obstáculo. O que segura o clique errado é a frase que
 * diz o que vai acontecer — que a coleta do cliente para, que as sessões caem,
 * que não há caminho de volta.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  consequence,
  confirmLabel,
  destructive = true,
  busy = false,
  error,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  consequence: ReactNode
  confirmLabel: string
  destructive?: boolean
  busy?: boolean
  error?: ReactNode
  onConfirm: () => void
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={consequence}
      footer={
        <>
          {error != null && error !== '' && (
            <p role="alert" className="mr-auto text-sm leading-snug text-crit">
              {error}
            </p>
          )}
          <RadixDialog.Close asChild>
            <Button variant="default">Cancelar</Button>
          </RadixDialog.Close>
          <Button variant={destructive ? 'danger' : 'primary'} busy={busy} onClick={onConfirm}>
            {busy ? 'Enviando…' : confirmLabel}
          </Button>
        </>
      }
    />
  )
}
